<script setup lang="ts">
import {computed} from "vue";
import {CircleAlert, Info, RotateCcw, Settings2} from "@lucide/vue";
import useAssistantSettingsForm from "@/ai-assistant/composables/useAssistantSettingsForm";
import {HEALTH_JOURNEY_GROUP} from "@/ai-assistant/constants";
import AssistantConstraintList from "@/ai-assistant/components/AssistantConstraintList.vue";
import AssistantParameterField from "@/ai-assistant/components/AssistantParameterField.vue";

const {assistantKey} = defineProps<{
    assistantKey: string;
}>();

const {
    schema, values, constraints, conflicts, subjects, isLoading, failed,
    editValue, editConstraints, restoreDefaults
} = useAssistantSettingsForm(assistantKey);

const caps = computed(() => schema.value.filter(field => field.group === 'caps'));

/**
 * Everything that is not a cap, minus the groups a screen of its own owns:
 * the health journey rules are edited from the journey screen, where the
 * plan they shape is on display, and listing them here too would offer the
 * same parameter twice with nothing to say which one won.
 */
const instructions = computed(() =>
    schema.value.filter(field => field.group !== 'caps' && field.group !== HEALTH_JOURNEY_GROUP)
);

/**
 * Reset what this panel shows, and nothing else: the health journey rules are
 * edited from their own screen, and a pharmacist clearing the daily caps here
 * has no reason to expect the spacings they set over there to go back to their
 * defaults without a word.
 */
const resetDisplayed = () => restoreDefaults(
    [...caps.value, ...instructions.value].map(field => field.key)
);

/** The line the mockup puts under the fields, so the state reads at a glance. */
const summary = computed<string>(() => {
    const capValues = caps.value.map(field => {
        const value = values.value[field.key];

        return typeof value === 'number' ? String(value) : '∞';
    });

    const custom = values.value.custom_instructions
        ? 'consignes personnalisées enregistrées'
        : 'aucune consigne personnalisée';

    return capValues.length
        ? `Plafonds : ${capValues.join(' / ')} par jour · ${custom}`
        : custom;
});
</script>

<template>
  <!--
    Scrolls on its own rather than pushing the composer out of the panel: the
    field list grows with the pharmacy's appointment types, and the conversation
    keeps its share because its flex basis is zero, so only this section yields.
  -->
  <section class="min-h-0 overflow-y-auto border-b border-primary-lightest bg-primary-lightest/40 px-4 py-3">
    <div class="mb-1 flex items-center gap-1.5">
      <Settings2 class="size-3.5 shrink-0 text-primary" />
      <p class="min-w-0 flex-1 text-xs font-semibold text-primary">
        Paramètres du copilote
      </p>
      <button
        class="flex shrink-0 items-center gap-1 text-[11px] text-primary/70 transition-colors hover:text-primary"
        @click="resetDisplayed"
      >
        <RotateCcw class="size-3" />
        Réinitialiser
      </button>
    </div>
    <p class="mb-2.5 text-[11px] leading-snug text-gray-dark/70">
      Plafonds quotidiens et consignes utilisés par l'IA pour ses propositions.
    </p>

    <p v-if="isLoading && !schema.length" class="text-[11px] text-gray-dark/60">
      Chargement des paramètres…
    </p>

    <div
      v-else-if="failed"
      class="flex items-start gap-1.5 rounded-lg bg-orange/20 px-2.5 py-2 text-[11px] leading-snug text-gray-dark"
    >
      <CircleAlert class="mt-px size-3.5 shrink-0 text-orange" />
      Les paramètres n'ont pas pu être chargés. Rouvrez le panneau pour réessayer.
    </div>

    <template v-else>
      <div v-if="caps.length" class="space-y-1">
        <AssistantParameterField
          v-for="field in caps"
          :key="field.key"
          :field="field"
          :model-value="values[field.key] ?? null"
          @update:model-value="editValue(field.key, $event)"
        />
      </div>

      <div class="mt-2.5">
        <AssistantConstraintList
          :constraints="constraints"
          :subjects="subjects"
          @change="editConstraints"
        />
      </div>

      <div v-for="field in instructions" :key="field.key" class="mt-2.5">
        <AssistantParameterField
          :field="field"
          :model-value="values[field.key] ?? null"
          @update:model-value="editValue(field.key, $event)"
        />
        <p v-if="field.type === 'textarea'" class="mt-1 text-[10.5px] leading-snug text-gray-dark/60">
          Ces consignes orientent les propositions sans les garantir. Pour qu'une règle soit
          toujours respectée, ajoutez-la aux contraintes garanties.
        </p>
      </div>

      <div
        v-for="conflict in conflicts"
        :key="`${conflict.kind}-${conflict.subject_id}`"
        class="mt-2 flex items-start gap-1.5 rounded-lg bg-orange/20 px-2.5 py-2 text-[11px] leading-snug text-gray-dark"
      >
        <CircleAlert class="mt-px size-3.5 shrink-0 text-orange" />
        {{ conflict.message }}
      </div>

      <p class="mt-2.5 flex items-start gap-1.5 text-[10.5px] leading-snug text-gray-dark/60">
        <Info class="mt-px size-3 shrink-0" />
        {{ summary }}
      </p>
    </template>
  </section>
</template>
